import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft } from 'lucide-react';

export default function PrivacyPage() {
  const navigate = useNavigate();

  const h2Class = "text-xl font-semibold text-slate-900 mb-3";
  const h3Class = "text-base font-semibold text-slate-800 mt-6 mb-2";
  const fontManrope = { fontFamily: 'Manrope' };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-100 sticky top-0 z-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900" data-testid="back-button">
            <ArrowLeft className="w-5 h-5" />
            <div className="w-8 h-8 rounded-lg bg-[#007AFF] flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <span className="text-xl font-bold text-slate-900" style={fontManrope}>AgentCheck</span>
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="privacy-policy">
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={fontManrope}>Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-2">Effective date: February 12, 2026</p>
        <p className="text-sm text-slate-500 mb-10">Last updated: February 19, 2026</p>

        <section className="space-y-10 text-slate-700 leading-relaxed text-[15px]">

          {/* ── 1. Introduction ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>1. Introduction</h2>
            <p>
              This Privacy Policy ("Policy") describes how Dispatch Global Services Inc, doing business as Sagisu
              ("Company," "we," "us," or "our"), collects, uses, stores, shares, and protects information when you
              use the AgentCheck website located at{' '}
              <a href="https://www.agentcheck.com" className="text-[#007AFF] underline" target="_blank" rel="noopener noreferrer">www.agentcheck.com</a>{' '}
              (the "Website"), the AgentCheck Monitor browser extension (the "Extension"), and any related services,
              tools, or APIs (collectively, the "Services").
            </p>
            <p className="mt-3">
              By accessing or using any of the Services, you acknowledge that you have read, understood, and agree
              to be bound by this Policy. If you do not agree, you must discontinue use of the Services immediately.
            </p>
            <p className="mt-3">
              AgentCheck is designed with a privacy-first, consent-first architecture. The Extension operates locally
              on your device by default and does not transmit your browsing data to our servers unless you explicitly
              opt into a feature that requires it.
            </p>
          </div>

          {/* ── 2. Definitions ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>2. Definitions</h2>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li><span className="font-medium text-slate-800">"Personal Data"</span> means any information that identifies or can reasonably be used to identify a natural person, directly or indirectly.</li>
              <li><span className="font-medium text-slate-800">"Processing"</span> means any operation performed on Personal Data, including collection, recording, organization, storage, adaptation, retrieval, use, disclosure, erasure, or destruction.</li>
              <li><span className="font-medium text-slate-800">"Third-Party Domain"</span> means a domain name that differs from the effective top-level domain plus one (eTLD+1) of the website you are visiting.</li>
              <li><span className="font-medium text-slate-800">"Agent Pressure Score" or "APS"</span> means a numerical score (0-100) computed from the volume, diversity, and risk profile of third-party domains detected on a scanned website.</li>
              <li><span className="font-medium text-slate-800">"Rollup Data"</span> means aggregated, domain-level request counts stored in the Extension's local IndexedDB database, grouped by hour bucket.</li>
              <li><span className="font-medium text-slate-800">"Scan Report"</span> means the structured analysis output generated when a user submits a URL for scanning via the Website.</li>
            </ul>
          </div>

          {/* ── 3. Information We Collect ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>3. Information We Collect</h2>

            <h3 className={h3Class}>3.1 AgentCheck Scan (Website)</h3>
            <p>When you submit a URL for scanning via the Website, we process and may store:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><span className="font-medium text-slate-800">Domain submitted:</span> The URL you provide is redacted to scheme and hostname only (e.g., "https://example.com"). Full URL paths, query parameters, and fragment identifiers are stripped before storage.</li>
              <li><span className="font-medium text-slate-800">Scan results:</span> Domain-level data about third-party requests observed during the scan, including third-party domain names, mapped company names, tracker categories, resource types, request counts, and the computed Agent Pressure Score.</li>
              <li><span className="font-medium text-slate-800">Agent capability signals:</span> Publicly available information from the scanned site's robots.txt file (AI bot directives), /llms.txt, /.well-known/ai-plugin.json, /.well-known/agents.json, /skills.md, and /soul.md files.</li>
              <li><span className="font-medium text-slate-800">Rate-limiting data:</span> A one-way cryptographic hash (SHA-256, truncated) of your IP address, used solely for rate limiting. The raw IP address is not stored.</li>
              <li><span className="font-medium text-slate-800">Timestamps:</span> Date and time of scan requests.</li>
            </ul>

            <p className="mt-4 font-medium text-slate-800">We do NOT collect or store from scans:</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>Page content, HTML source, or rendered text of scanned websites</li>
              <li>Form inputs, passwords, or authentication credentials</li>
              <li>Cookie values, session tokens, or local storage data from scanned sites</li>
              <li>Request or response bodies from third-party network traffic</li>
              <li>Your raw IP address (only a one-way hash is stored)</li>
            </ul>

            <p className="mt-4"><span className="font-medium text-slate-800">Scan caching:</span> Scan results for the same domain may be cached for up to six (6) hours to improve performance and reduce redundant scanning. Subsequent requests for the same domain within this period will receive the cached result.</p>

            <p className="mt-3"><span className="font-medium text-slate-800">Public reports:</span> Each scan generates a report accessible via a unique, randomly generated URL token (e.g., /r/rpt_xxxxx). Reports contain domain-level aggregate data only. No full URLs, page content, or user-identifying information appears in public reports. You control whether to share the report link.</p>

            <h3 className={h3Class}>3.2 AgentCheck Monitor (Chrome Extension)</h3>
            <p>
              The AgentCheck Monitor Extension is <span className="font-medium text-slate-900">disabled by default</span>.
              No data collection occurs until you explicitly enable monitoring and grant the required browser permissions.
            </p>

            <p className="mt-3 font-medium text-slate-800">3.2.1 Permissions requested</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><span className="font-medium text-slate-800">Required at install:</span> <code className="bg-slate-100 px-1 rounded text-sm">storage</code> (for local settings), <code className="bg-slate-100 px-1 rounded text-sm">alarms</code> (for scheduled cleanup), <code className="bg-slate-100 px-1 rounded text-sm">declarativeNetRequest</code> (for domain blocking rules).</li>
              <li><span className="font-medium text-slate-800">Requested only when you enable monitoring:</span> <code className="bg-slate-100 px-1 rounded text-sm">webRequest</code> (to observe network requests) and host permissions for web pages. These are requested at runtime via Chrome's permission prompt and can be revoked at any time.</li>
            </ul>
            <p className="mt-2">The Extension does not declare any content scripts. No JavaScript code is injected into web pages you visit.</p>

            <p className="mt-4 font-medium text-slate-800">3.2.2 Data stored locally on your device</p>
            <p className="mt-1">When monitoring is enabled, the Extension stores the following data in your browser's local IndexedDB database. This data never leaves your device:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><span className="font-medium text-slate-800">Rollup records:</span> Aggregated counts of third-party requests grouped by hour bucket, first-party domain (eTLD+1 only, e.g., "nytimes.com"), third-party domain, company name, and tracker category.</li>
              <li><span className="font-medium text-slate-800">Consent preferences:</span> Your monitoring scope (single site or all sites), enabled/disabled state, and selected scope domains.</li>
              <li><span className="font-medium text-slate-800">Blocking rules:</span> A list of companies you have blocked or paused, including domain lists and expiration times for temporary pauses.</li>
              <li><span className="font-medium text-slate-800">Cache entries:</span> Pre-computed summary aggregates for display performance.</li>
            </ul>

            <p className="mt-4 font-medium text-slate-800">3.2.3 Data NOT collected by the Extension</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>Full URLs, page paths, or query strings (only the eTLD+1 domain is stored)</li>
              <li>Page content, text, images, or rendered DOM elements</li>
              <li>Form inputs, passwords, or authentication credentials</li>
              <li>Cookie values, session tokens, or local/session storage data</li>
              <li>Request or response headers (including User-Agent strings)</li>
              <li>Request or response bodies</li>
              <li>Keystroke, mouse movement, or interaction data</li>
              <li>Geolocation or device sensor data</li>
              <li>Browser history, bookmarks, or tab information beyond the active tab's domain</li>
              <li>Personally identifiable information of any kind</li>
            </ul>

            <p className="mt-4 font-medium text-slate-800">3.2.4 Data transmission</p>
            <p className="mt-1">
              In its current version, the Extension does not transmit any browsing data, rollup data, or monitoring
              data to AgentCheck servers or any third party. All processing occurs locally in the browser's service
              worker. If a future version introduces optional cloud synchronization, we will update this Policy,
              provide clear disclosure, and require your explicit opt-in consent before any data leaves your device.
            </p>

            <p className="mt-4 font-medium text-slate-800">3.2.5 Data retention and deletion</p>
            <ul className="list-disc pl-6 mt-1 space-y-1">
              <li>Rollup data is automatically purged after seven (7) days via a daily scheduled cleanup alarm.</li>
              <li>You may export all local data as a JSON file at any time via Settings &gt; Export Data.</li>
              <li>You may delete all local data at any time via Settings &gt; Delete All Data.</li>
              <li>Uninstalling the Extension removes all locally stored data.</li>
            </ul>

            <h3 className={h3Class}>3.3 Account and Contact Information</h3>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><span className="font-medium text-slate-800">Waitlist:</span> If you join the Monitor waitlist, we collect your email address and the referral source. Email addresses are stored in normalized (lowercase) form and deduplicated.</li>
              <li><span className="font-medium text-slate-800">Support inquiries:</span> If you contact us via email, we collect your email address, name (if provided), and the content of your message.</li>
            </ul>

            <h3 className={h3Class}>3.4 Payment Information</h3>
            <p>
              Subscription payments are processed by Stripe, Inc. ("Stripe"), a PCI DSS Level 1 certified payment
              processor. We do not receive, process, or store your full credit or debit card number, CVV, or
              expiration date. We receive and store only:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Stripe checkout session identifiers</li>
              <li>Subscription status (active, canceled, past due)</li>
              <li>Plan type and billing period</li>
              <li>A device identifier (locally generated, not linked to personal identity)</li>
              <li>Transaction amounts and timestamps</li>
            </ul>
            <p className="mt-2">Stripe's privacy policy governs their handling of your payment data: <a href="https://stripe.com/privacy" className="text-[#007AFF] underline" target="_blank" rel="noopener noreferrer">https://stripe.com/privacy</a>.</p>

            <h3 className={h3Class}>3.5 Automatically Collected Technical Data</h3>
            <p>When you visit the Website, our hosting infrastructure may automatically collect:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>IP address (used for rate limiting; stored only as a one-way hash)</li>
              <li>Browser type and version</li>
              <li>Operating system</li>
              <li>Referring URL</li>
              <li>Pages visited and time spent</li>
              <li>Date and time of access</li>
            </ul>
            <p className="mt-2">This data is used for operational purposes (security, abuse prevention, performance monitoring) and is not combined with other data to identify individual users.</p>
          </div>

          {/* ── 4. How We Use Information ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>4. How We Use Information</h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><span className="font-medium text-slate-800">Service delivery:</span> To generate scan reports, compute Agent Pressure Scores, display third-party tracker analysis, and provide Extension functionality.</li>
              <li><span className="font-medium text-slate-800">User controls:</span> To execute blocking rules (Shut/Pause/Continue), maintain rule state, and display monitoring timelines and summaries.</li>
              <li><span className="font-medium text-slate-800">Security and abuse prevention:</span> To enforce rate limits, detect automated abuse, prevent unauthorized access, and protect the integrity of the Services.</li>
              <li><span className="font-medium text-slate-800">Product improvement:</span> To analyze aggregate, non-identifying usage patterns to improve functionality, performance, and reliability of the Services.</li>
              <li><span className="font-medium text-slate-800">Communication:</span> To respond to support inquiries, send waitlist notifications, and provide service-related updates.</li>
              <li><span className="font-medium text-slate-800">Legal compliance:</span> To comply with applicable laws, regulations, legal processes, or enforceable governmental requests.</li>
              <li><span className="font-medium text-slate-800">Payment processing:</span> To manage subscriptions, verify entitlements, and process billing through our payment processor.</li>
            </ul>
          </div>

          {/* ── 5. Legal Bases for Processing ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>5. Legal Bases for Processing (EEA/UK Users)</h2>
            <p>If you are located in the European Economic Area (EEA) or the United Kingdom (UK), we process your Personal Data under the following legal bases as defined by the General Data Protection Regulation (GDPR):</p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li><span className="font-medium text-slate-800">Consent (Article 6(1)(a)):</span> When you enable monitoring in the Extension, join the waitlist, or opt into future cloud synchronization features.</li>
              <li><span className="font-medium text-slate-800">Performance of a contract (Article 6(1)(b)):</span> When we process data necessary to provide the Services you have requested, including scan reports and subscription management.</li>
              <li><span className="font-medium text-slate-800">Legitimate interests (Article 6(1)(f)):</span> For security, abuse prevention, rate limiting, and product improvement, where our interests do not override your fundamental rights and freedoms.</li>
              <li><span className="font-medium text-slate-800">Legal obligation (Article 6(1)(c)):</span> When we are required to process data to comply with applicable law.</li>
            </ul>
          </div>

          {/* ── 6. Sharing and Disclosure ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>6. Sharing and Disclosure of Information</h2>
            <p className="font-medium text-slate-900">We do not sell, rent, or trade your Personal Data to third parties for their marketing purposes.</p>
            <p className="mt-3">We may share information in the following limited circumstances:</p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li><span className="font-medium text-slate-800">Service providers:</span> We engage trusted third-party vendors to operate the Services, including cloud hosting providers, database services (MongoDB), payment processing (Stripe), and browser automation infrastructure (Playwright/Chromium). These providers process data on our behalf under contractual obligations to maintain confidentiality and security.</li>
              <li><span className="font-medium text-slate-800">Public scan reports:</span> If you choose to share a scan report link, the report contents (domain-level data only) are accessible to anyone with that link. You control whether to distribute the link.</li>
              <li><span className="font-medium text-slate-800">Legal requirements:</span> We may disclose information if required to do so by law, regulation, subpoena, court order, or other legal process, or if we believe in good faith that disclosure is necessary to protect our rights, your safety, the safety of others, investigate fraud, or respond to a government request.</li>
              <li><span className="font-medium text-slate-800">Business transfers:</span> In the event of a merger, acquisition, reorganization, bankruptcy, or sale of all or a portion of our assets, your information may be transferred as part of that transaction. We will notify you via email or prominent notice on the Website before your information becomes subject to a different privacy policy.</li>
              <li><span className="font-medium text-slate-800">With your consent:</span> We may share information for purposes not described in this Policy if we have obtained your explicit consent.</li>
            </ul>
          </div>

          {/* ── 7. Data Storage, Retention, and Security ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>7. Data Storage, Retention, and Security</h2>

            <h3 className={h3Class}>7.1 Extension data (local storage)</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>All Extension monitoring data is stored exclusively in your browser's local IndexedDB database and Chrome storage.</li>
              <li>Rollup data is automatically deleted after seven (7) days.</li>
              <li>Blocking rules persist until you remove them or uninstall the Extension.</li>
              <li>You may delete all data at any time. Uninstalling the Extension removes all data.</li>
            </ul>

            <h3 className={h3Class}>7.2 Server-side data</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Scan reports are retained on our servers for a limited period to support caching (six hours for repeat scans) and shared report access.</li>
              <li>Rate-limiting hashes are ephemeral and held in application memory only.</li>
              <li>Waitlist email addresses are retained until the feature launches or you request removal.</li>
              <li>Payment metadata is retained as required for accounting, tax, and dispute resolution purposes.</li>
            </ul>

            <h3 className={h3Class}>7.3 Security measures</h3>
            <p className="mt-1">
              We implement reasonable technical and organizational measures to protect the information we handle, including:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Encryption in transit (TLS/HTTPS for all communications)</li>
              <li>One-way hashing of IP addresses (SHA-256) before storage</li>
              <li>URL redaction (paths and query strings stripped) before storage</li>
              <li>No storage of raw IP addresses, passwords, or payment card details</li>
              <li>Rate limiting and abuse detection on public API endpoints</li>
              <li>Principle of least privilege for browser extension permissions</li>
            </ul>
            <p className="mt-3">
              Despite these measures, no method of electronic storage or transmission is 100% secure. We cannot
              guarantee absolute security, and you use the Services at your own risk.
            </p>
          </div>

          {/* ── 8. Your Rights and Choices ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>8. Your Rights and Choices</h2>

            <h3 className={h3Class}>8.1 All users</h3>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><span className="font-medium text-slate-800">Enable/disable monitoring:</span> Toggle monitoring on or off at any time within the Extension.</li>
              <li><span className="font-medium text-slate-800">Scope control:</span> Choose whether monitoring applies to a single site or all sites.</li>
              <li><span className="font-medium text-slate-800">Revoke permissions:</span> Remove Extension permissions via Chrome's extension settings at any time.</li>
              <li><span className="font-medium text-slate-800">Export data:</span> Download all locally stored Extension data as a JSON file.</li>
              <li><span className="font-medium text-slate-800">Delete data:</span> Delete all locally stored Extension data with one click, or uninstall the Extension.</li>
              <li><span className="font-medium text-slate-800">Manage blocking rules:</span> Add, remove, or modify which companies are blocked at any time.</li>
              <li><span className="font-medium text-slate-800">Opt out of communications:</span> Unsubscribe from non-essential emails by contacting us or using the unsubscribe mechanism provided.</li>
            </ul>

            <h3 className={h3Class}>8.2 EEA/UK residents (GDPR rights)</h3>
            <p className="mt-1">Under the General Data Protection Regulation, you have the right to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><span className="font-medium text-slate-800">Access:</span> Request a copy of the Personal Data we hold about you.</li>
              <li><span className="font-medium text-slate-800">Rectification:</span> Request correction of inaccurate or incomplete Personal Data.</li>
              <li><span className="font-medium text-slate-800">Erasure:</span> Request deletion of your Personal Data ("right to be forgotten"), subject to legal retention requirements.</li>
              <li><span className="font-medium text-slate-800">Restriction:</span> Request that we restrict processing of your Personal Data under certain circumstances.</li>
              <li><span className="font-medium text-slate-800">Portability:</span> Receive your Personal Data in a structured, commonly used, machine-readable format.</li>
              <li><span className="font-medium text-slate-800">Object:</span> Object to processing based on legitimate interests.</li>
              <li><span className="font-medium text-slate-800">Withdraw consent:</span> Withdraw previously given consent at any time without affecting the lawfulness of prior processing.</li>
              <li><span className="font-medium text-slate-800">Lodge a complaint:</span> File a complaint with your local data protection authority (supervisory authority).</li>
            </ul>

            <h3 className={h3Class}>8.3 California residents (CCPA/CPRA rights)</h3>
            <p className="mt-1">Under the California Consumer Privacy Act (CCPA) as amended by the California Privacy Rights Act (CPRA), California residents have the right to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li><span className="font-medium text-slate-800">Know:</span> Request disclosure of the categories and specific pieces of Personal Data we have collected.</li>
              <li><span className="font-medium text-slate-800">Delete:</span> Request deletion of Personal Data we have collected, subject to certain exceptions.</li>
              <li><span className="font-medium text-slate-800">Correct:</span> Request correction of inaccurate Personal Data.</li>
              <li><span className="font-medium text-slate-800">Opt out of sale or sharing:</span> We do not sell or share your Personal Data as defined by the CCPA/CPRA.</li>
              <li><span className="font-medium text-slate-800">Non-discrimination:</span> We will not discriminate against you for exercising your privacy rights.</li>
            </ul>
            <p className="mt-2">To exercise these rights, contact us at <a href="mailto:support@agentcheck.com" className="text-[#007AFF] underline">support@agentcheck.com</a>. We will respond within the timeframes required by applicable law.</p>

            <h3 className={h3Class}>8.4 Other jurisdictions</h3>
            <p className="mt-1">
              If you reside in a jurisdiction with applicable data protection laws (including but not limited to
              Brazil's LGPD, Canada's PIPEDA, Australia's Privacy Act 1988, or India's DPDP Act 2023), you may have
              additional rights regarding your Personal Data. Please contact us to exercise any rights available
              under your local law.
            </p>
          </div>

          {/* ── 9. Cookies and Tracking Technologies ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>9. Cookies and Tracking Technologies</h2>
            <p>
              The AgentCheck Website does not use third-party analytics cookies, advertising cookies, or tracking pixels.
            </p>
            <p className="mt-2">
              We may use essential, first-party cookies or local storage strictly necessary for the operation of the
              Services (e.g., caching scan configuration, storing UI preferences). These do not track you across
              websites and do not collect Personal Data.
            </p>
            <p className="mt-2">
              The Extension uses Chrome's <code className="bg-slate-100 px-1 rounded text-sm">chrome.storage.local</code> API
              and the browser's IndexedDB API for local data storage. These are browser-native storage mechanisms
              scoped to the Extension and are not accessible by websites or other extensions.
            </p>
          </div>

          {/* ── 10. Third-Party Services ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>10. Third-Party Services</h2>
            <p>The Services integrate with the following third-party services:</p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li><span className="font-medium text-slate-800">Stripe, Inc.</span> — Payment processing for subscriptions. Subject to Stripe's <a href="https://stripe.com/privacy" className="text-[#007AFF] underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</li>
              <li><span className="font-medium text-slate-800">Cloud hosting provider</span> — Server infrastructure for the Website and API. Scan reports and operational data are stored on servers operated by our hosting provider.</li>
              <li><span className="font-medium text-slate-800">MongoDB, Inc.</span> — Database services for storing scan reports, waitlist entries, and operational data.</li>
            </ul>
            <p className="mt-3">
              We require that all third-party service providers maintain appropriate security measures and process
              data only as instructed by us and in accordance with applicable law. We do not authorize these providers
              to use your data for their own purposes.
            </p>
          </div>

          {/* ── 11. International Data Transfers ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>11. International Data Transfers</h2>
            <p>
              Our servers and service providers may be located in the United States or other countries outside your
              country of residence. If you access the Services from outside the United States, your information may
              be transferred to, stored, and processed in the United States or other jurisdictions where our service
              providers operate.
            </p>
            <p className="mt-2">
              For transfers from the EEA/UK, we rely on appropriate legal mechanisms, which may include Standard
              Contractual Clauses (SCCs) approved by the European Commission, adequacy decisions, or other lawful
              transfer mechanisms as required by applicable law.
            </p>
            <p className="mt-2">
              By using the Services, you acknowledge and consent to the transfer and processing of your information
              in jurisdictions that may have different data protection laws than your country of residence.
            </p>
          </div>

          {/* ── 12. Children's Privacy ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>12. Children's Privacy</h2>
            <p>
              The Services are not directed to individuals under the age of thirteen (13), or under the age of
              sixteen (16) in jurisdictions where that is the applicable age of digital consent. We do not
              knowingly collect Personal Data from children.
            </p>
            <p className="mt-2">
              If we become aware that we have inadvertently collected Personal Data from a child under the applicable
              minimum age, we will take reasonable steps to delete such data promptly. If you believe that a child
              has provided us with Personal Data, please contact us at{' '}
              <a href="mailto:support@agentcheck.com" className="text-[#007AFF] underline">support@agentcheck.com</a>.
            </p>
          </div>

          {/* ── 13. Do Not Track ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>13. Do Not Track Signals</h2>
            <p>
              Some browsers transmit "Do Not Track" (DNT) signals. There is no uniform industry standard for
              responding to DNT signals. However, because the AgentCheck Website does not use third-party tracking
              cookies or cross-site tracking technologies, our practices are functionally consistent with DNT
              preferences regardless of the signal received.
            </p>
          </div>

          {/* ── 14. Open Source ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>14. Open-Source Components</h2>
            <p>
              The Services may incorporate open-source software libraries and components, each governed by their
              respective licenses. The use of these components does not affect our obligations under this Policy
              with respect to your Personal Data.
            </p>
          </div>

          {/* ── 15. Data Breach Notification ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>15. Data Breach Notification</h2>
            <p>
              In the event of a data breach that affects your Personal Data, we will notify you and any applicable
              regulatory authorities as required by law. Notification will be made within the timeframes mandated
              by applicable data protection regulations (e.g., seventy-two (72) hours under GDPR for supervisory
              authorities, or as otherwise required).
            </p>
            <p className="mt-2">
              Notification may be provided via email (if we have your email address), prominent notice on the
              Website, or through the Extension, as appropriate.
            </p>
          </div>

          {/* ── 16. Limitation of Liability ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>16. Limitation of Liability and Disclaimers</h2>
            <p>
              The Services are provided on an "as-is" and "as-available" basis. To the fullest extent permitted
              by applicable law:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li>We make no warranties, express or implied, regarding the accuracy, completeness, reliability, or availability of scan results, Agent Pressure Scores, tracker classifications, or any other output of the Services.</li>
              <li>Scan results reflect a point-in-time snapshot of third-party network activity detected during automated browsing and may not represent the complete set of trackers or agents on any given website.</li>
              <li>The Extension's blocking functionality operates on a best-effort basis using Declarative Net Request rules. We do not guarantee that all requests to blocked domains will be intercepted.</li>
              <li>We are not responsible for the privacy practices, data collection, or content of third-party websites you scan or visit.</li>
              <li>We disclaim all liability for any damages arising from your reliance on scan results, Agent Pressure Scores, or blocking effectiveness, including but not limited to direct, indirect, incidental, special, consequential, or punitive damages.</li>
              <li>Tracker and company classifications are based on publicly available information and a curated catalog. We make no representation that classifications are exhaustive, current, or error-free.</li>
            </ul>
          </div>

          {/* ── 17. Indemnification ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>17. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless Dispatch Global Services Inc (d/b/a Sagisu),
              its officers, directors, employees, agents, and affiliates from and against any and all claims,
              damages, losses, liabilities, costs, and expenses (including reasonable attorneys' fees) arising
              out of or related to your use of the Services, your violation of this Policy, or your violation
              of any rights of a third party.
            </p>
          </div>

          {/* ── 18. Governing Law and Dispute Resolution ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>18. Governing Law and Dispute Resolution</h2>
            <p>
              This Policy and any disputes arising from or related to the Services shall be governed by and
              construed in accordance with the laws of the State of Delaware, United States, without regard
              to its conflict of law principles.
            </p>
            <p className="mt-2">
              Any dispute, claim, or controversy arising out of or relating to this Policy or the Services that
              cannot be resolved through good-faith negotiation shall be settled by binding arbitration administered
              in accordance with the rules of the American Arbitration Association (AAA). The arbitration shall be
              conducted in the English language and the arbitral award shall be final and binding.
            </p>
            <p className="mt-2">
              Nothing in this section shall prevent either party from seeking injunctive or equitable relief in
              a court of competent jurisdiction for the protection of intellectual property rights or confidential
              information.
            </p>
            <p className="mt-2">
              If you are a consumer residing in the EEA/UK, the mandatory consumer protection provisions of your
              country of residence shall apply to the extent required by law, and you retain the right to bring
              proceedings in the courts of your country of residence.
            </p>
          </div>

          {/* ── 19. Severability ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>19. Severability</h2>
            <p>
              If any provision of this Policy is held to be invalid, illegal, or unenforceable by a court of
              competent jurisdiction, the remaining provisions shall continue in full force and effect. The invalid
              provision shall be modified to the minimum extent necessary to make it valid and enforceable while
              preserving the original intent.
            </p>
          </div>

          {/* ── 20. Changes to This Policy ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>20. Changes to This Policy</h2>
            <p>
              We may update this Policy from time to time to reflect changes in our practices, the Services,
              or applicable law. When we make material changes, we will:
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Update the "Last updated" date at the top of this page</li>
              <li>Post the revised Policy on the Website</li>
              <li>For material changes that affect how we handle Personal Data, provide notice via email (if we have your email address), a prominent banner on the Website, or a notification in the Extension</li>
            </ul>
            <p className="mt-2">
              Your continued use of the Services after the effective date of any updated Policy constitutes
              your acceptance of the changes. If you do not agree to the updated Policy, you must stop using
              the Services.
            </p>
          </div>

          {/* ── 21. Contact Information ── */}
          <div>
            <h2 className={h2Class} style={fontManrope}>21. Contact Information</h2>
            <p>
              If you have questions, concerns, or requests regarding this Policy or our data practices, please
              contact us:
            </p>
            <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-4">
              <p className="font-medium text-slate-900">Dispatch Global Services Inc</p>
              <p className="text-slate-700">d/b/a Sagisu</p>
              <p className="mt-2 text-slate-700">
                Email: <a href="mailto:support@agentcheck.com" className="text-[#007AFF] underline">support@agentcheck.com</a>
              </p>
              <p className="text-slate-700">
                Website: <a href="https://www.agentcheck.com" className="text-[#007AFF] underline" target="_blank" rel="noopener noreferrer">www.agentcheck.com</a>
              </p>
            </div>
            <p className="mt-4 text-sm text-slate-500">
              For data protection inquiries specifically related to GDPR or other data protection regulations,
              please include "Data Protection Request" in the subject line of your email.
            </p>
          </div>

        </section>
      </main>

      <footer className="py-8 border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-sm text-slate-500">
            AgentCheck is a product of Dispatch Global Services Inc (d/b/a Sagisu) |{' '}
            <button onClick={() => navigate('/')} className="text-[#007AFF] hover:underline">Home</button>
          </p>
        </div>
      </footer>
    </div>
  );
}
